import React, { useState } from 'react';
import {
    Box,
    Modal,
    Typography,
    TextField,
    CircularProgress,
    useMediaQuery,
    Stack,
    Button,
    Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { ActionButton, CancelButton } from '../../../components/Button';

interface ApprovalDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (remark: string, file?: File) => void;
    isLoading?: boolean;
    actionType: 'approve' | 'reject' | 'pass' | 'fail';
}

const ApprovalDialog: React.FC<ApprovalDialogProps> = ({
    open,
    onClose,
    onConfirm,
    isLoading = false,
    actionType,
}) => {
    const [remark, setRemark] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [remarkError, setRemarkError] = useState(false);
    const isMobile = useMediaQuery('(max-width:600px)');

    const handleConfirm = () => {
        // Validate that remark is required for approve and reject actions
        if ((actionType === 'approve' || actionType === 'reject') && !remark.trim()) {
            setRemarkError(true);
            return; // Don't proceed if remark is empty for mandatory actions
        }
        
        setRemarkError(false);
        onConfirm(remark.trim(), selectedFile || undefined);
        setRemark(''); // Reset remark after confirmation
        setSelectedFile(null); // Reset file after confirmation
    };

    const handleClose = () => {
        setRemark(''); // Reset remark when closing
        setSelectedFile(null); // Reset file when closing
        setRemarkError(false); // Reset error state when closing
        onClose();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        setSelectedFile(file);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
    };

    const handleRemarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRemark(e.target.value);
        // Clear error when user starts typing
        if (remarkError) {
            setRemarkError(false);
        }
    };

    const getDialogContent = () => {
        switch (actionType) {
            case 'approve':
                return {
                    title: 'Approve Approval',
                    description: 'Please provide a reason for approving this approval.',
                    placeholder: 'Enter approval reason (required)...',
                    buttonText: 'Approve',
                    isRequired: true
                };
            case 'reject':
                return {
                    title: 'Reject Approval',
                    description: 'Please provide a reason for rejecting this approval.',
                    placeholder: 'Enter rejection reason (required)...',
                    buttonText: 'Reject',
                    isRequired: true
                };
            case 'pass':
                return {
                    title: 'Pass Review',
                    description: 'You can optionally provide a reason for passing this review.',
                    placeholder: 'Enter pass reason (optional)...',
                    buttonText: 'Pass',
                    isRequired: false
                };
            case 'fail':
                return {
                    title: 'Fail Review',
                    description: 'You can optionally provide a reason for failing this review.',
                    placeholder: 'Enter fail reason (optional)...',
                    buttonText: 'Fail',
                    isRequired: false
                };
            default:
                return {
                    title: 'Update Status',
                    description: 'You can optionally provide a reason for this action.',
                    placeholder: 'Enter reason (optional)...',
                    buttonText: 'Submit',
                    isRequired: false
                };
        }
    };

    const { title, description, placeholder, buttonText, isRequired } = getDialogContent();

    const style = {
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: '#fff',
        borderRadius: '32px',
        boxShadow: 24,
        px: isMobile ? 3 : 4,
        py: isMobile ? 4 : 5,
        width: isMobile ? '85%' : 500,
        maxWidth: 500,
        maxHeight: '80vh',
        overflow: 'auto',
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={style}>
                <Stack spacing={isMobile ? 3 : 4}>
                    {/* Title */}
                    <Typography
                        fontFamily='Poppins, sans-serif'
                        fontWeight={600}
                        fontSize={{ xs: '18px', sm: '20px' }}
                        lineHeight='28px'
                        color='#132F5F'
                        textAlign='center'
                    >
                        {title}
                    </Typography>

                    {/* Description */}
                    <Typography
                        fontFamily='Poppins, sans-serif'
                        fontSize={{ xs: '13px', sm: '14px' }}
                        color='#49454F'
                        lineHeight='20px'
                        textAlign='center'
                    >
                        {description}
                    </Typography>

                    {/* Text Field */}
                    <TextField
                        autoFocus
                        multiline
                        rows={isMobile ? 6 : 4}
                        fullWidth
                        variant='outlined'
                        label={isRequired ? 'Remark *' : 'Remark'}
                        placeholder={placeholder}
                        value={remark}
                        onChange={handleRemarkChange}
                        error={remarkError}
                        helperText={remarkError ? 'This field is required for this action' : ''}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: { xs: '13px', sm: '14px' },
                                '& fieldset': {
                                    borderColor: remarkError ? '#DC2626' : '#E0E0E0',
                                },
                                '&:hover fieldset': {
                                    borderColor: remarkError ? '#DC2626' : '#132F5F',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: remarkError ? '#DC2626' : '#132F5F',
                                },
                            },
                            '& .MuiInputBase-input': {
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: { xs: '13px', sm: '14px' },
                                padding: { xs: '12px 16px', sm: '16px 20px' },
                            },
                            '& .MuiFormHelperText-root': {
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: { xs: '11px', sm: '12px' },
                                color: '#DC2626',
                                marginLeft: 0,
                            },
                        }}
                    />

                    {/* File Upload Section */}
                    <Box>
                        <Typography
                            fontFamily='Poppins, sans-serif'
                            fontSize={{ xs: '13px', sm: '14px' }}
                            fontWeight={500}
                            color='#132F5F'
                            mb={1}
                        >
                            Attach File (Optional)
                        </Typography>
                        
                        {selectedFile ? (
                            <Chip
                                label={selectedFile.name}
                                onDelete={handleRemoveFile}
                                deleteIcon={<DeleteIcon />}
                                sx={{
                                    maxWidth: '100%',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: { xs: '12px', sm: '13px' },
                                    backgroundColor: '#E8F5E8',
                                    color: '#2E7D2E',
                                    '& .MuiChip-deleteIcon': {
                                        color: '#2E7D2E',
                                        '&:hover': {
                                            color: '#1B5E1B',
                                        },
                                    },
                                }}
                            />
                        ) : (
                            <Button
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: { xs: '12px', sm: '13px' },
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    color: '#132F5F',
                                    borderColor: '#132F5F',
                                    borderRadius: '8px',
                                    border: '1px solid',
                                    px: 2,
                                    py: 1,
                                    '&:hover': {
                                        borderColor: '#0f254d',
                                        backgroundColor: 'rgba(19, 47, 95, 0.04)',
                                    },
                                }}
                            >
                                Choose File
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileChange}
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />
                            </Button>
                        )}
                    </Box>

                    {/* Buttons */}
                    <Stack 
                        direction={isMobile ? 'column' : 'row'} 
                        spacing={isMobile ? 2 : 3}
                        justifyContent='center'
                        alignItems='stretch'
                    >
                        <CancelButton
                            onClick={handleClose}
                            disabled={isLoading}
                            sx={{
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: { xs: 14, sm: 16 },
                                fontWeight: 500,
                                textTransform: 'none',
                                borderRadius: '12px',
                                px: { xs: 3, sm: 4 },
                                height: { xs: 48, sm: 56 },
                                width: isMobile ? '100%' : 'auto',
                                minWidth: isMobile ? '100%' : 120,
                            }}
                        >
                            Cancel
                        </CancelButton>
                        <ActionButton
                            onClick={handleConfirm}
                            disabled={isLoading}
                            sx={{
                                backgroundColor: '#132F5F',
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: { xs: 14, sm: 16 },
                                fontWeight: 500,
                                textTransform: 'none',
                                borderRadius: '12px',
                                px: { xs: 3, sm: 4 },
                                height: { xs: 48, sm: 56 },
                                width: isMobile ? '100%' : 'auto',
                                minWidth: isMobile ? '100%' : 120,
                                '&:hover': {
                                    backgroundColor: '#0f254d',
                                },
                                '&:disabled': {
                                    backgroundColor: '#ccc',
                                },
                            }}
                        >
                            {isLoading ? <CircularProgress size={20} color='inherit' /> : buttonText}
                        </ActionButton>
                    </Stack>
                </Stack>
            </Box>
        </Modal>
    );
};

export default ApprovalDialog;
